import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from './ui/dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import {
  DollarSign, Heart, Loader2, CheckCircle, Sparkles
} from 'lucide-react';
import { cn } from '../lib/utils';

const PRESETS = [
  { amount: 1, label: '$1', color: 'from-emerald-600/20 to-emerald-500/10 border-emerald-500/30' },
  { amount: 5, label: '$5', color: 'from-blue-600/20 to-blue-500/10 border-blue-500/30' },
  { amount: 10, label: '$10', color: 'from-violet-600/20 to-violet-500/10 border-violet-500/30' },
  { amount: 25, label: '$25', color: 'from-amber-600/20 to-amber-500/10 border-amber-500/30' },
];

export default function TipJarModal({ open, onOpenChange, postId, creatorName }) {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [selected, setSelected] = useState(5);
  const [custom, setCustom] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const amount = useCustom ? parseFloat(custom) || 0 : selected;
  const isValid = amount >= 1 && amount <= 500;

  const handleTip = async () => {
    if (!isAuthenticated) {
      toast.info('Sign in to send tips');
      return;
    }
    if (!isValid) {
      toast.error('Enter a valid amount ($1 - $500)');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/monetization/tip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ post_id: postId, amount, note: note || null }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
        setSuccess(true);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send tip');
      }
    } catch {
      toast.error('Failed to send tip');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm" data-testid="tip-success-modal">
          <div className="flex flex-col items-center py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h3 className="text-xl font-bold mb-1">Tip Sent!</h3>
            <p className="text-sm text-muted-foreground">
              ${amount.toFixed(2)} to {creatorName || 'this creator'}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="tip-jar-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-500" />
            Send a Tip
          </DialogTitle>
          <DialogDescription>
            Support {creatorName || 'this creator'} directly
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Preset amounts */}
          <div className="grid grid-cols-4 gap-2">
            {PRESETS.map(p => (
              <button
                key={p.amount}
                onClick={() => { setSelected(p.amount); setUseCustom(false); }}
                className={cn(
                  'py-3 rounded-xl border text-center font-bold transition-all bg-gradient-to-b',
                  !useCustom && selected === p.amount
                    ? `${p.color} scale-105 shadow-md`
                    : 'border-border hover:border-border bg-muted/30'
                )}
                data-testid={`tip-preset-${p.amount}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Custom amount */}
          <div>
            <button
              onClick={() => setUseCustom(true)}
              className={cn(
                'w-full text-left text-xs mb-1.5 font-medium transition-colors',
                useCustom ? 'text-violet-400' : 'text-muted-foreground/60 hover:text-muted-foreground/80'
              )}
            >
              Custom amount
            </button>
            {useCustom && (
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  min="1"
                  max="500"
                  step="0.01"
                  placeholder="Enter amount"
                  value={custom}
                  onChange={e => setCustom(e.target.value)}
                  className="pl-8"
                  data-testid="tip-custom-input"
                  autoFocus
                />
              </div>
            )}
          </div>

          {/* Optional note */}
          <Textarea
            placeholder="Add a note (optional)"
            value={note}
            onChange={e => setNote(e.target.value)}
            className="min-h-[60px] text-sm"
            maxLength={200}
            data-testid="tip-note"
          />

          {/* Summary */}
          {isValid && (
            <Card className="p-3 bg-muted/30 border-border">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tip amount</span>
                <span className="font-bold">${amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>Creator receives (90%)</span>
                <span>${(amount * 0.9).toFixed(2)}</span>
              </div>
            </Card>
          )}

          {/* Send button */}
          <Button
            className="w-full"
            size="lg"
            disabled={!isValid || loading}
            onClick={handleTip}
            data-testid="send-tip-button"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            {loading ? 'Processing...' : `Send $${isValid ? amount.toFixed(2) : '0.00'} Tip`}
          </Button>

          <p className="text-[10px] text-center text-muted-foreground">
            Secure payment via Stripe. 10% platform fee.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
